<script>
	import { each } from "svelte/internal";
	import { scaleLinear } from "d3-scale";

	export let type = "bar";
	export let id;
	export let row;
	export let dataCharts;
	export let dataText;
	$: key = row.measure;
	// console.log("row", row);
	// console.log("key", key);
	// console.log("dataCharts", dataCharts);

	$: thisChart = dataCharts.map((e) => e[key]).filter((e) => e);
	// console.log(thisChart);

	let max = row.yaxisend;
	let min = row.yaxisstart;
	let range = max - min;

	let chartWidth = 300,
		height = 110;
	let el;

	$: barWidth = 9 * ((chartWidth - 104) / ((thisChart.length - 1) * 10 + 9));
	// console.log(barWidth);

	$: yScale = scaleLinear().domain([min, max]).range([0, height]);

	$: decimalPlacesRaw = row?.decimalPlaces ?? row?.decimalPlace;
	$: decimalPlaces = Number.isFinite(+decimalPlacesRaw)
		? Math.max(0, Math.min(20, Math.trunc(+decimalPlacesRaw)))
		: 1;

	function formatWithDecimalPlaces(value) {
		const numberValue = +value;
		return Number.isFinite(numberValue)
			? numberValue.toFixed(decimalPlaces)
			: value;
	}

	function formatDate(dateStr) {
		if (!dateStr) return dateStr;

		const str = String(dateStr);

		const monthMap = {
			jan: "Jan",
			feb: "Feb",
			mar: "Mar",
			apr: "Apr",
			may: "May",
			jun: "June",
			jul: "July",
			aug: "Aug",
			sep: "Sept",
			oct: "Oct",
			nov: "Nov",
			dec: "Dec",
		};

		// Handle dd-mmm-yy format (e.g. "15-Jan-25") → "15 Jan 2025"
		const longMatch = str.match(/^(\d{1,2})-([A-Za-z]+)-(\d{2})$/);
		if (longMatch) {
			const month = monthMap[longMatch[2].toLowerCase()];
			const year = 2000 + parseInt(longMatch[3]);
			if (!month) return str;
			return `${parseInt(longMatch[1])} ${month} ${year}`;
		}

		// Handle dd-mmm format (e.g. "26-Apr") → "26 Apr"
		const noYearMatch = str.match(/^(\d{1,2})-([A-Za-z]+)$/);
		if (noYearMatch) {
			const month = monthMap[noYearMatch[2].toLowerCase()];
			if (!month) return str;
			return `${parseInt(noYearMatch[1])} ${month}`;
		}

		// Handle mmm-yy format (e.g. "Jan-25") → "Jan 2025"
		const shortMatch = str.match(/^([A-Za-z]+)-(\d{2})$/);
		if (shortMatch) {
			const month = monthMap[shortMatch[1].toLowerCase()];
			const year =
				parseInt(shortMatch[2]) >= 30
					? 1900 + parseInt(shortMatch[2])
					: 2000 + parseInt(shortMatch[2]);
			if (!month) return str;
			return `${month} ${year}`;
		}

		return str;
	}
	$: formattedStartDate = formatDate(row.startdate);
	$: formattedEndDate = formatDate(row.enddate);
	$: formattedUpdated = formatDate(row.update);
</script>

<div class="graphic" {id} bind:this={el} bind:clientWidth={chartWidth}>
	<!-- Chart heading text -->
	<div class="chartContainer">
		<p class="headingText">{row.headingtext}</p>
	</div>

	<svg class="chartDrawing" width="281">
		<!-- This bit draws the chart background -->
		<rect
			class="chartBackground"
			x="35"
			width={chartWidth - 104}
			height="110"
		></rect>
		<!-- Y-axis -->
		<line
			x1="35"
			x2="35"
			y1="0"
			y2="110"
			stroke="black"
			stroke-width="0.5px"
		/>
		<line x1="30" x2="35" y1="0" y2="0" stroke="grey" />
		<line x1="30" x2="35" y1="110" y2="110" stroke="grey" />
		<text
			x="30"
			y="0"
			fill="black"
			text-anchor="end"
			dominant-baseline="middle">{row.yaxisend}</text
		>
		<text
			x="30"
			y="110"
			fill="black"
			text-anchor="end"
			dominant-baseline="middle">{row.yaxisstart}</text
		>

		<!-- X-axis -->
		<!-- X-axis line (if desired)-->
		{#if !row.yaxisstart}
			<line
				x1="35"
				x2={35 + (chartWidth - 104)}
				y1="110"
				y2="110"
				stroke="lightgrey"
				stroke-width="3px"
			/>
		{/if}

		<line
			x1={35 + (chartWidth - 104) - barWidth / 2}
			x2={35 + (chartWidth - 104) - barWidth / 2}
			y1="110"
			y2="115"
			stroke="grey"
		/>
		<line
			x1={35 + barWidth / 2}
			x2={35 + barWidth / 2}
			y1="110"
			y2="115"
			stroke="grey"
		/>
		<text
			class="startDate"
			x={35 + barWidth / 2}
			y="110"
			dy="1.5em"
			dominant-baseline="top">{formattedStartDate}</text
		>
		<text
			class="endDate"
			x={35 + (chartWidth - 104) - barWidth / 2}
			y="110"
			dy="1.5em"
			dominant-baseline="top">{formattedEndDate}</text
		>

		<!-- Bars-->
		{#each thisChart as point, i}
			<rect
				x={35 + (i * barWidth * 10) / 9}
				y={height - yScale(point)}
				width={barWidth}
				height={yScale(point)}
				fill="rgb(32, 96, 149)"
			/>
		{/each}

		<!--text label-->
		<text
			class="endText"
			x={25 + (chartWidth - 104)}
			y={height -
				((thisChart[thisChart.length - 1] - min) / range) * height}
			fill="rgb(51, 51, 51)"
			text-anchor="start"
			dx="1em"
			dominant-baseline="middle"
			font-weight="700"
			>{formatWithDecimalPlaces(thisChart[thisChart.length - 1])}
			{row.directLabelUnit?.includes("%")
				? row.directLabelUnit
				: " " + row.directLabelUnit}</text
		>
	</svg>
	<p class="updated">Updated: {formattedUpdated}</p>
</div>
